import React from 'react'
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Stack
} from '@mui/material'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import LightModeIcon from '@mui/icons-material/LightMode'
import MenuIcon from '@mui/icons-material/Menu'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import { useReduxAuth } from '../contexts/ReduxAuthContext'
import { saveLanguagePreference } from '../../i18n/i18n'
import { useSelector } from 'react-redux'
import type { RootState } from '../../store/store'

type NavigationProps = {
  mode: 'light' | 'dark'
  onToggleTheme: () => void
}

const Navigation: React.FC<NavigationProps> = ({ mode, onToggleTheme }) => {
  const { t, i18n } = useTranslation()
  const { authenticated, user, logout } = useReduxAuth()
  const { accessTokenExpiresAt, refreshTokenExpiresAt } = useSelector((state: RootState) => state.auth)
  const location = useLocation()
  const [languageAnchor, setLanguageAnchor] = React.useState<null | HTMLElement>(null)
  const [mobileAnchor, setMobileAnchor] = React.useState<null | HTMLElement>(null)
  const mobileMenuId = React.useId()
  const [sessionSecondsLeft, setSessionSecondsLeft] = React.useState<number | null>(null)

  // Log Navigation state changes
  React.useEffect(() => {
    console.log('🔒 Navigation state changed:', { authenticated, user: !!user, location: location.pathname })
  }, [authenticated, user, location.pathname])

  const baseLinks = React.useMemo(() => ([
    { label: t('nav.home'), to: '/' },
  ]), [t])

  const isAdmin = React.useMemo(() => {
    return user?.roles?.includes('admin') || false
  }, [user])

  const formatSessionLeft = (totalSeconds: number) => {
    const clamped = Math.max(0, totalSeconds)
    const hours = Math.floor(clamped / 3600)
    const minutes = Math.floor((clamped % 3600) / 60)
    const seconds = clamped % 60
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}`
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  React.useEffect(() => {
    if (!authenticated) {
      setSessionSecondsLeft(null)
      return
    }
    const expiresAt = refreshTokenExpiresAt ?? accessTokenExpiresAt ?? null
    if (!expiresAt) {
      setSessionSecondsLeft(null)
      return
    }
    const update = () => {
      const diffMs = Math.max(0, expiresAt - Date.now())
      setSessionSecondsLeft(Math.ceil(diffMs / 1000))
    }
    update()
    const interval = window.setInterval(update, 1000)
    return () => window.clearInterval(interval)
  }, [authenticated, accessTokenExpiresAt, refreshTokenExpiresAt])

  const userLinks = React.useMemo(() => ([
    { label: t('nav.myArea', 'Mein Bereich'), to: '/admin' },
    { label: t('addPoem'), to: '/admin/poem/create' },
  ]), [t])

  const adminOnlyLinks = React.useMemo(() => ([
    { label: t('nav.admin', 'Admin'), to: '/admin-panel' },
  ]), [t])

  const renderNavButton = (link: { label: string; to: string }) => (
    <Button
      key={link.to}
      color="inherit"
      component={RouterLink}
      to={link.to}
      sx={{
        fontWeight: location.pathname === link.to ? 700 : 500,
        textTransform: 'none',
      }}
      onClick={() => setMobileAnchor(null)}
    >
      {link.label}
    </Button>
  )

  return (
    <AppBar
      position="sticky"
      color="transparent"
      elevation={0}
      sx={{ backdropFilter: 'blur(12px)', borderBottom: '1px solid', borderColor: 'divider' }}
    >
      <Toolbar sx={{ gap: 2 }}>
        <Typography
          variant="h6"
          component={RouterLink}
          to="/"
          sx={{
            flexGrow: 1,
            fontWeight: 900,
            textDecoration: 'none',
            color: 'inherit',
          }}
        >
          Kavithai.de
        </Typography>

        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
          {baseLinks.map(renderNavButton)}
          {authenticated && userLinks.map(renderNavButton)}
          {authenticated && isAdmin && adminOnlyLinks.map(renderNavButton)}
        </Box>

        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
          <IconButton
            color="inherit"
            onClick={(e) => setMobileAnchor(e.currentTarget)}
            aria-label={t('nav.openMenu') || 'Open navigation menu'}
            aria-haspopup="true"
            aria-expanded={Boolean(mobileAnchor)}
            aria-controls={mobileMenuId}
          >
            <MenuIcon />
          </IconButton>
          <Menu
            anchorEl={mobileAnchor}
            open={Boolean(mobileAnchor)}
            onClose={() => setMobileAnchor(null)}
            keepMounted
            id={mobileMenuId}
          >
            <MenuItem sx={{ justifyContent: 'flex-end' }} onClick={() => setMobileAnchor(null)}>
              <IconButton
                size="small"
                aria-label={t('nav.closeMenu') || 'Close navigation menu'}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </MenuItem>
            {baseLinks.map(renderNavButton)}
            {authenticated && userLinks.map(renderNavButton)}
            {authenticated && isAdmin && adminOnlyLinks.map(renderNavButton)}
          </Menu>
        </Box>

        <IconButton
          color="inherit"
          onClick={onToggleTheme}
          title={mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
          aria-label={
            mode === 'light'
              ? t('nav.switchToDarkMode') || 'Switch to dark mode'
              : t('nav.switchToLightMode') || 'Switch to light mode'
          }
        >
          {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
        </IconButton>

        <IconButton
          color="inherit"
          onClick={(e) => setLanguageAnchor(e.currentTarget)}
          aria-label={t('nav.languageMenuLabel') || 'Change language'}
          aria-haspopup="true"
          aria-expanded={Boolean(languageAnchor)}
        >
          🌐
        </IconButton>
        <Menu anchorEl={languageAnchor} open={Boolean(languageAnchor)} onClose={() => setLanguageAnchor(null)}>
          <MenuItem
            onClick={() => { i18n.changeLanguage('ta'); saveLanguagePreference('ta'); setLanguageAnchor(null) }}
            selected={i18n.language === 'ta'}
          >
            தமிழ்
          </MenuItem>
          <MenuItem
            onClick={() => { i18n.changeLanguage('de'); saveLanguagePreference('de'); setLanguageAnchor(null) }}
            selected={i18n.language === 'de'}
          >
            Deutsch
          </MenuItem>
          <MenuItem
            onClick={() => { i18n.changeLanguage('en'); saveLanguagePreference('en'); setLanguageAnchor(null) }}
            selected={i18n.language === 'en'}
          >
            English
          </MenuItem>
        </Menu>

        {authenticated && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 1 }}>
            <Typography variant="body2" sx={{ opacity: 0.75 }}>
              {user?.username || user?.email || user?.name || 'admin'}
            </Typography>
            {sessionSecondsLeft !== null && (
              <Typography variant="caption" sx={{ opacity: 0.6 }}>
                {t('auth.sessionTimer', 'Session')}: {formatSessionLeft(sessionSecondsLeft)}
              </Typography>
            )}
            <Button
              color="primary"
              variant="outlined"
              onClick={async () => {
                console.log('🔒 Navigation - logout button clicked')
                try {
                  await logout()
                } catch (error) {
                  console.error('Logout failed:', error)
                }
              }}
            >
              {t('nav.logout')}
            </Button>
          </Stack>
        )}
      </Toolbar>

    </AppBar>
  )
}

export default Navigation
